// src/pages/index.jsx
import React from "react";
import { Container, Button, useMediaQuery, Grid } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useTranslation } from 'react-i18next';

export default function Home() {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const mobile = useMediaQuery((t) => t.breakpoints.down("sm"));
    const tablet = useMediaQuery((t) => t.breakpoints.down("md"));

    const BRAND = {
        darkPurple: "#3B235D",
        textDark: "#111827",
        headerHeroBg: "#F5F5F5",
    };

    const services = [
        {
            no: "1",
            title: t('services.title-1'),
            desc: t('services.desc-1'),
            type: "white",
        },
        {
            no: "2",
            title: t('services.title-2'),
            desc: t('services.desc-2'),
            type: "dark",
        },
        {
            no: "3",
            title: t('services.title-3'),
            desc: t('services.desc-3'),
            type: "mid",
        },
        {
            no: "4",
            title: t('services.title-4'),
            desc: t('services.desc-4'),
            type: "white",
        },
        {
            no: "5",
            title: t('services.title-5'),
            desc: t('services.desc-5'),
            type: "white",
        },
    ];

    const philosophy = [
        {
            img: "/images/philosophy_1.jpg",
            text: t('philosophy.phi-1')
        },
        {
            img: "/images/philosophy_2.jpg",
            text: t('philosophy.phi-2')
        },
        {
            img: "/images/philosophy_3.jpg",
            text: t('philosophy.phi-3')
        },
    ];

    // ====== SERVICES (Figma padding tuning)
    const CARD_PAD = mobile ? 20 : 30;

    const S = {
        page: {
            backgroundColor: "#F5F5F5",
            minHeight: "100vh",
        },

        // HERO
        heroWrap: {
            background: BRAND.headerHeroBg,
            padding: mobile ? "0 0 24px" : "0 0 34px",
        },
        heroOuter: { width: "100%" },
        heroImage: {
            position: "relative",
            borderRadius: 18,
            overflow: "hidden",
            minHeight: mobile ? 520 : 700,
        },
        heroBg: {
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
        },
        heroOverlay: { position: "absolute", inset: 0, background: "transparent" },
        heroInner: {
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: mobile ? "0 16px" : "0 24px",
            textAlign: "center",
            color: "white",
        },
        heroContent: {
            width: "100%",
            maxWidth: mobile ? 540 : 980,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        },
        heroTitle: {
            margin: 0,
            fontWeight: 800,
            letterSpacing: -0.6,
            lineHeight: 1.1,
            fontSize: mobile ? 34 : 60,
            color: "#FFFFFF",
            textShadow: '#0000003b 2px 2px 2px',
        },
        heroSub: {
            marginTop: 14,
            marginBottom: 0,
            color: "rgba(255, 255, 255, 1)",
            fontSize: mobile ? 13 : 15,
            lineHeight: 1.6,
            maxWidth: 780,
            textShadow: '#0000008f 1px 1px 1px',
        },
        heroBtns: {
            display: "flex",
            justifyContent: "center",
            gap: 14,
            marginTop: 24,
            flexWrap: "wrap",
        },
        btnPill: {
            borderRadius: 999,
            textTransform: "none",
            padding: mobile ? "10px 18px" : "12px 22px",
            fontWeight: 700,
            boxShadow: "none",
            minWidth: 140,
        },

        // Sections
        section: { padding: mobile ? "56px 0" : "80px 0" },
        bigHeading: {
            margin: 0,
            fontWeight: 800,
            letterSpacing: -1,
            fontSize: mobile ? 52 : 76,
            color: BRAND.textDark,
            fontFamily: "Georgia, 'Times New Roman', serif",
        },

        // ===== SERVICES LAYOUT (keep your grid)
        servicesGrid: {
            marginTop: 24,
            display: "grid",
            gridTemplateColumns: mobile ? "1fr" : "360px 1fr 320px",
            gap: 22,
            alignItems: "stretch",
            gridAutoRows: "minmax(220px, auto)",
        },

        // Cards
        serviceCardBase: {
            borderRadius: 18,
            padding: CARD_PAD,
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 14px 40px rgba(12, 15, 25, 0.06)",
            position: "relative",
            overflow: "hidden",
            background: "rgba(255,255,255,0.92)",
        },

        serviceCardDark: {
            borderRadius: 18,
            padding: CARD_PAD,
            border: "0px solid rgba(0,0,0,0.06)",
            boxShadow: "0 14px 40px rgba(12, 15, 25, 0.06)",
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(180deg, #D7C2F3 0%, #462E5E 100%)",
            color: "#fff",
        },

        serviceCardMid: {
            borderRadius: 18,
            padding: CARD_PAD,
            border: "0px solid rgba(0,0,0,0.06)",
            boxShadow: "0 14px 40px rgba(12, 15, 25, 0.06)",
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(180deg, #A9ACE3 0%, #462E5E 100%)",
            color: "#fff",
        },

        serviceNo: (isWhite) => ({
            // position: "absolute",
            // top: mobile ? 18 : 26,
            // left: mobile ? 18 : 26,
            fontWeight: 800,
            fontSize: mobile ? 26 : 34, // bigger like Figma
            lineHeight: 1,
            fontFamily: "Abhaya Libre ExtraBold",
            color: isWhite ? "var(--text-color)" : "rgba(255, 255, 255, 1)",
        }),

        serviceTitle: (isWhite) => ({
            margin: 0,
            marginTop: 80,
            fontSize: mobile ? 18 : 20,
            fontWeight: 800,
            color: isWhite ? BRAND.textDark : "#fff",
            fontFamily: "Georgia, 'Times New Roman', serif",
            lineHeight: 1.2,
            whiteSpace: "pre-line",
        }),

        serviceDesc: (isWhite) => ({
            marginTop: 14,
            color: isWhite ? "#4b5563" : "rgba(255, 255, 255, 1)",
            lineHeight: 1.85,
            textAlign: "justify",
            fontSize: mobile ? 13 : 14,
            letterSpacing: 0.2,
        }),

        serviceImageCard: {
            borderRadius: 18,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 14px 40px rgba(12, 15, 25, 0.06)",
            position: "relative",
            background: "#000",
            minHeight: 260,
        },
        serviceImage: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
        },

        serviceCardFlex: {
            display: "flex",
            flexDirection: "column",
            height: "100%",
        },

        serviceBodyBottom: {
            marginTop: "auto", // 👈 push title+desc to bottom
        },

        // Figma-ish placement using grid positions (desktop)
        // 1: col1 row1
        // 2: spans col2-3 row1
        // 4: col1 row2-3
        // 3: col2 row2
        // image: col3 row2
        // 5: spans col2-3 row3
        gridPos: (key) => {
            if (mobile) return {};
            switch (key) {
                case "1":
                    return { gridColumn: "1 / 2", gridRow: "1 / 2" };
                case "2":
                    return { gridColumn: "2 / 4", gridRow: "1 / 2" };
                case "3":
                    return { gridColumn: "1 / 2", gridRow: "2 / 4" };
                case "4":
                    return { gridColumn: "2 / 4", gridRow: "2 / 3" };
                case "IMG":
                    return { gridColumn: "3 / 4", gridRow: "2 / 3" };
                case "5":
                    return { gridColumn: "2 / 4", gridRow: "3 / 4" };
                default:
                    return {};
            }
        },

        // PHILOSOPHY
        philosophyGrid: { marginTop: 24, display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(3, 1fr)", gap: 60 },
        phCard: {
            borderRadius: 18,
            fontSize: 15,
            overflow: "hidden",
            background: "white",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 14px 40px rgba(12, 15, 25, 0.06)",
            textAlign: "justify",
        },
        phImg: { width: 359, height: 271, objectFit: "cover" },
        phText: { margin: 0, padding: 25, color: "#374151", lineHeight: 1.3 },

        // CONCLUSION
        // conclusionRow: {
        //     marginTop: 10,
        //     display: "grid",
        //     gridTemplateColumns: mobile ? "1fr" : "420px 1fr",
        //     gap: 26,
        //     alignItems: "center",
        // },
        // conclusionImg: {
        //     width: 453,
        //     borderRadius: 22,
        //     objectFit: "cover",
        //     boxShadow: "0 18px 45px rgba(12, 15, 25, 0.12)",
        //     maxHeight: 675,
        // },
        conclusionText: { color: "#4b5563", lineHeight: 1.75, maxWidth: 720 },

        footer: { borderTop: "1px solid rgba(0,0,0,0.06)", padding: "22px 0", color: "#6b7280", fontSize: 13 },
        footerRow: { display: "flex", flexDirection: mobile ? "column" : "row", justifyContent: "space-between", gap: 12 },
        footerLinks: { display: "flex", gap: 14, flexWrap: "wrap" },
        footerA: { color: "#6b7280", textDecoration: "none" },
    };

    const getServiceCardStyle = (type) => {
        if (type === "dark") return S.serviceCardDark;
        if (type === "mid") return S.serviceCardMid;
        return S.serviceCardBase;
    };

    const isWhiteCard = (type) => type === "white";

    return (
        <div style={S.page}>
            {/* HERO */}
            <div style={S.heroWrap}>
                <Container maxWidth={false} disableGutters style={{ background: BRAND.headerHeroBg }}>
                    <div style={S.heroOuter}>
                        <div style={S.heroImage}>
                            <img style={S.heroBg} src="/images/title.jpg" alt="hero" />
                            <div style={S.heroOverlay} />

                            <div style={S.heroInner}>
                                <div style={S.heroContent}>
                                    <h1 style={S.heroTitle}>{t('title.title')}
                                    </h1>
                                    <p style={S.heroSub}>{t('title.subtitle')}
                                    </p>

                                    <div style={S.heroBtns}>
                                        <Button
                                            variant="contained"
                                            href="#service"
                                            disableElevation
                                            style={{ ...S.btnPill, background: "#FFFFFF", color: "#111827" }}
                                        >
                                            {t('button.getstarted')}

                                        </Button>

                                        {/*
                                        <Button
                                            variant="contained"
                                            href="#contact"
                                            disableElevation
                                            style={{ ...S.btnPill, background: BRAND.darkPurple, color: "#FFFFFF" }}
                                        >
                                            Contact Us
                                        </Button>
                                        */}

                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </Container>
            </div>

            {/* ABOUT */}
            <Container>
                <section id="about" style={S.section}>
                    <div className="flex-sb-m w-full gap-25">
                        <div className="lh-17 txt-justify" style={{width: tablet ? '100%' : '70%', color: "#4b5563", whiteSpace: "pre-line",}}>
                            <h2 style={S.bigHeading}>{t('about.title')}</h2>
                            <p>{t('about.desc')}</p>
                        </div>
                        {!tablet &&
                            <div className="flex-c-m op-09 p-t-80">
                                <img src="/images/X.png" alt="logo icon" style={{width: 500, maxWidth: '30%'}}/>
                            </div>
                        }
                    </div>
                </section>
            </Container>



            {/* SERVICES */}
            <Container id="service" >
                <h2 style={S.bigHeading}>{t('services.title')}</h2>
                <Grid container spacing={4} style={{ paddingTop: 10 }}>
                    <Grid item xs={12} sm={6} md={6} lg={4}>
                        {/* Card 1 */}
                        {(() => {
                            const s = services.find((x) => x.no === "1");
                            const white = isWhiteCard(s.type);
                            return (
                                <div className="flex-col-sb" style={{ ...getServiceCardStyle(s.type), ...S.gridPos("1"), ...S.serviceCardFlex, height: '100%' }}>
                                    <div style={S.serviceNo(white)}><p>{s.no}</p></div>
                                    <div>
                                        <h3 style={S.serviceTitle(white)}>{s.title}</h3>
                                        <p style={S.serviceDesc(white)}>{s.desc}</p>
                                    </div>
                                </div>
                            );
                        })()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={8}>
                        {/* Card 2 (span) */}
                        {(() => {
                            const s = services.find((x) => x.no === "2");
                            const white = isWhiteCard(s.type);
                            return (
                                <div className="flex-col-sb" style={{ ...getServiceCardStyle(s.type), ...S.gridPos("2"), ...S.serviceCardFlex, height: '100%' }}>
                                    <div style={S.serviceNo(white)}><p>{s.no}</p></div>
                                    <div>
                                        <h3 style={S.serviceTitle(white)}>{s.title}</h3>
                                        <p style={S.serviceDesc(white)}>{s.desc}</p>
                                    </div>
                                </div>
                            );
                        })()}
                    </Grid>
                </Grid>
                <Grid container spacing={4} style={{ marginTop: 10 }}>
                    <Grid item xs={12} sm={12} md={4} style={{ height: 'auto' }}>
                        {/* Card 3 */}
                        {(() => {
                            const s = services.find((x) => x.no === "3");
                            const white = isWhiteCard(s.type);
                            return (
                                <div className="flex-col-sb" style={{ ...getServiceCardStyle(s.type), ...S.gridPos("3"), ...S.serviceCardFlex, height: '100%' }}>
                                    <div style={S.serviceNo(white)}>{s.no}</div>

                                    <div style={S.serviceBodyBottom}>
                                        <h3 style={S.serviceTitle(white) }>{s.title}</h3>
                                        <p style={S.serviceDesc(white)}>{s.desc}</p>
                                    </div>
                                </div>
                            );
                        })()}

                    </Grid>
                    <Grid item xs={12} sm={12} md={8}>
                        <Grid container spacing={4} style={{ alignItems: 'stretch' }}>
                            <Grid item xs={12} sm={6} md={8} style={{ height: 'auto' }}>
                                {/* Card 4 (tall left) */}
                                {(() => {
                                    const s = services.find((x) => x.no === "4");
                                    const white = isWhiteCard(s.type);
                                    return (
                                        <div className="flex-col-sb"  style={{ ...getServiceCardStyle(s.type), ...S.gridPos("4"), ...S.serviceCardFlex, height: '100%' }}>
                                            <div style={S.serviceNo(white)}>{s.no}</div>
                                            
                                            <div>
                                                <h3 style={S.serviceTitle(white)}>{s.title}</h3>
                                                <p style={S.serviceDesc(white)}>{s.desc}</p>
                                            </div>
                                        </div>
                                    );
                                })()}

                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                {/* Image card */}
                                {!mobile &&
                                    <div style={{ ...S.serviceImageCard, ...S.gridPos("IMG"), height: '100%' }}>
                                        <img
                                            src="/images/services.jpg"
                                            alt="service visual"
                                            className="s-full"
                                            style={{
                                                ...S.serviceImage,
                                                height: mobile ? "280px" : undefined,
                                                objectFit: "cover",
                                                display: "block",
                                                width: "100%",
                                            }}
                                        />
                                    </div>
                                }
                            </Grid>
                            <Grid item xs={12}>
                                {/* Card 5 (span) */}
                                {(() => {
                                    const s = services.find((x) => x.no === "5");
                                    const white = isWhiteCard(s.type);
                                    return (
                                        <div className="flex-col-sb"  style={{ ...getServiceCardStyle(s.type), ...S.gridPos("5") }}>
                                            <div style={S.serviceNo(white)}>{s.no}</div>
                                            <div>
                                                <h3 style={S.serviceTitle(white)}>{s.title}</h3>
                                                <p style={S.serviceDesc(white)}>{s.desc}</p>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Container>

            {/* PHILOSOPHY */}
            <Container>
                <section id="philosophy" style={S.section}>
                    <h2 style={S.bigHeading}> {t('philosophy.phi-title')} </h2>

                    <Grid container spacing={3} className="m-t-24">
                        {philosophy.map((item, index) => (
                            <Grid key={index} item xs={12} md={4}>
                                <div className="card-white">
                                    <img src={item.img} alt={`philosophy-${index}`} className="w-full" style={{ height: mobile ? '220px' : '271px', objectFit: "cover" }} />
                                    <p className="txt-justify p-all-30 fs-14 lh-16" style={{ color: "#374151" }}>
                                        {item.text}
                                    </p>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                </section>
            </Container>

            {/* CONCLUSION + CONTACT */}
            <Container>
                <section id="conclusion" style={S.section}>
                    <div className={`${tablet ? 'flex-col-c-m' : 'flex-c-m'} gap-25`}>
                        <div className="max-w-full" style={{width: tablet? '100%' : '420px'}} >
                            <img src="/images/conclusion.jpg" alt="conclusion" className="bor25"  style={{width: tablet? '100%' : '420px', boxShadow: "0 18px 45px rgba(12, 15, 25, 0.12)"}} />
                        </div>
                        <div>
                            <h2 className="p-b-24" style={S.bigHeading}>{t('conclusion.title')} </h2>
                            <p style={S.conclusionText}> {t("conclusion.desc")}
                            </p>

                            {/*
                            <div id="contact" style={{ marginTop: 18 }}>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    style={{
                                        borderRadius: 999,
                                        textTransform: "none",
                                        padding: "12px 22px",
                                        fontWeight: 700,
                                        background: BRAND.darkPurple,
                                        color: "#FFFFFF",
                                    }}
                                >
                                    Contact Us
                                </Button>
                            </div>
                            */}

                        </div>
                    </div>
                </section>
            </Container>

            {/* FOOTER */}
            <div style={S.footer}>
                <Container>
                    <div style={S.footerRow}>
                        <div>© {new Date().getFullYear()} {t('title.company')}. All rights reserved.</div>
                        {/*
                        <div style={S.footerLinks}>
                            <a style={S.footerA} href="/privacy">Privacy Policy</a>
                            <a style={S.footerA} href="/terms">Terms of Use</a>
                            <a style={S.footerA} href="/exchange">Exchange Policy</a>
                        </div>
                        */}
                    </div>
                </Container>
            </div>
        </div>
    );
}
